import React, { useEffect, useState } from 'react';
import {
  getWelcomeBonusSettings,
  updateWelcomeBonusSettings,
  getAllPromotions,
  createPromotion,
  updatePromotion,
  deletePromotion,
  applyPromotion,
  grantFreeSubscription,
  getFreeSubscriptionLogs,
  getFreeSubscriptionStatistics,
  getUserDevices,
  getDeviceUsers,
  checkUserEligibility,
  detectAbuse
} from '../services/freeSubscriptions';

const FreeSubscriptions = () => {
  const [activeTab, setActiveTab] = useState('welcome-bonus');
  const [loading, setLoading] = useState(false);
  
  const [welcomeBonus, setWelcomeBonus] = useState({ days: 0, enabled: false });
  const [promotions, setPromotions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [statistics, setStatistics] = useState(null);
  
  const [userDevices, setUserDevices] = useState(null);
  const [deviceUsers, setDeviceUsers] = useState(null);
  const [eligibilityResult, setEligibilityResult] = useState(null);
  const [abuseDevices, setAbuseDevices] = useState([]);
  
  const [deviceSearch, setDeviceSearch] = useState({ userId: '', email: '' });
  const [deviceId, setDeviceId] = useState('');
  const [eligibilitySearch, setEligibilitySearch] = useState({ userId: '', email: '', promotionId: '' });
  
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  
  const [promotionForm, setPromotionForm] = useState({
    promotion_name: '',
    description: '',
    free_days: 3,
    recipient_filter: 'all',
    start_date: '',
    end_date: '',
    is_active: true,
    auto_apply: false,
    max_redemptions: null
  });
  
  const [grantForm, setGrantForm] = useState({
    user_ids: '',
    free_days: 7,
    reason: ''
  });

  useEffect(() => {
    if (activeTab === 'welcome-bonus') {
      fetchWelcomeBonus();
    } else if (activeTab === 'promotions') {
      fetchPromotions();
    } else if (activeTab === 'logs') {
      fetchLogs();
    } else if (activeTab === 'statistics') {
      fetchStatistics();
    }
  }, [activeTab]);

  const fetchWelcomeBonus = async () => {
    try {
      setLoading(true);
      const response = await getWelcomeBonusSettings();
      setWelcomeBonus(response.settings || { days: 0, enabled: false });
    } catch (error) {
      console.error('Failed to fetch welcome bonus:', error);
      alert('Failed to load welcome bonus settings: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateWelcomeBonus = async () => {
    try {
      await updateWelcomeBonusSettings(welcomeBonus);
      alert('Welcome bonus settings updated successfully!');
      fetchWelcomeBonus();
    } catch (error) {
      alert('Failed to update settings: ' + error.message);
    }
  };

  const fetchPromotions = async () => {
    try {
      setLoading(true);
      const response = await getAllPromotions();
      setPromotions(response.promotions || response.data || []);
    } catch (error) {
      console.error('Failed to fetch promotions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePromotion = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...promotionForm,
        max_redemptions: promotionForm.max_redemptions ? parseInt(promotionForm.max_redemptions) : null
      };
      
      if (editingPromotion) {
        await updatePromotion(editingPromotion.id, data);
        alert('Promotion updated successfully!');
      } else {
        await createPromotion(data);
        alert('Promotion created successfully!');
      }
      
      setShowPromotionModal(false);
      setEditingPromotion(null);
      resetPromotionForm();
      fetchPromotions();
    } catch (error) {
      alert('Failed to save promotion: ' + error.message);
    }
  };

  const resetPromotionForm = () => {
    setPromotionForm({
      promotion_name: '',
      description: '',
      free_days: 3,
      recipient_filter: 'all',
      start_date: '',
      end_date: '',
      is_active: true,
      auto_apply: false,
      max_redemptions: null
    });
  };

  const handleDeletePromotion = async (id) => {
    if (!window.confirm('Delete this promotion?')) return;
    try {
      await deletePromotion(id);
      fetchPromotions();
    } catch (error) {
      alert('Failed to delete promotion: ' + error.message);
    }
  };

  const handleApplyPromotion = async (id) => {
    if (!window.confirm('Apply this promotion to all eligible users?')) return;
    try {
      setLoading(true);
      const response = await applyPromotion(id);
      alert(`Promotion applied!\nTotal: ${response.total}\nSuccessful: ${response.successful}\nFailed: ${response.failed}`);
      fetchPromotions();
    } catch (error) {
      alert('Failed to apply promotion: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGrantFreeSubscription = async (e) => {
    e.preventDefault();
    try {
      const userIds = grantForm.user_ids.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      if (userIds.length === 0) {
        alert('Please enter valid user IDs');
        return;
      }

      const data = {
        user_ids: userIds,
        free_days: parseInt(grantForm.free_days),
        reason: grantForm.reason
      };

      const response = await grantFreeSubscription(data);
      alert(`Free subscriptions granted!\nTotal: ${response.total}\nSuccessful: ${response.successful}\nFailed: ${response.failed}`);
      setShowGrantModal(false);
      setGrantForm({ user_ids: '', free_days: 7, reason: '' });
    } catch (error) {
      alert('Failed to grant free subscription: ' + error.message);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await getFreeSubscriptionLogs({ limit: 100 });
      setLogs(response.logs || response.data || []);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await getFreeSubscriptionStatistics();
      setStatistics(response.stats || response.data || []);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchUserDevices = async () => {
    try {
      setLoading(true);
      const userId = deviceSearch.userId || '0';
      const email = deviceSearch.email || null;
      const response = await getUserDevices(userId, email);
      setUserDevices(response);
    } catch (error) {
      alert('Failed to search user devices: ' + error.message);
      setUserDevices(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchDeviceUsers = async () => {
    try {
      setLoading(true);
      const response = await getDeviceUsers(deviceId);
      setDeviceUsers(response);
    } catch (error) {
      alert('Failed to search device users: ' + error.message);
      setDeviceUsers(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckEligibility = async () => {
    try {
      setLoading(true);
      const userId = eligibilitySearch.userId || '0';
      const email = eligibilitySearch.email || null;
      const response = await checkUserEligibility(userId, eligibilitySearch.promotionId, email);
      setEligibilityResult(response);
    } catch (error) {
      alert('Failed to check eligibility: ' + error.message);
      setEligibilityResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDetectAbuse = async () => {
    try {
      setLoading(true);
      const response = await detectAbuse(2);
      setAbuseDevices(response.devices || []);
    } catch (error) {
      alert('Failed to detect abuse: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl md:text-3xl font-bold">Free Subscription Management</h1>
        {activeTab === 'promotions' && (
          <button
            onClick={() => {
              setEditingPromotion(null);
              resetPromotionForm();
              setShowPromotionModal(true);
            }}
            className="bg-blue-600 text-white px-4 md:px-6 py-2 rounded-lg hover:bg-blue-700 text-sm md:text-base w-full sm:w-auto"
          >
            + Create Promotion
          </button>
        )}
        {activeTab === 'welcome-bonus' && (
          <button
            onClick={() => setShowGrantModal(true)}
            className="bg-green-600 text-white px-4 md:px-6 py-2 rounded-lg hover:bg-green-700 text-sm md:text-base w-full sm:w-auto"
          >
            Manual Grant
          </button>
        )}
      </div>

      <div className="mb-6 flex gap-2 md:gap-4 border-b overflow-x-auto">
        <button
          onClick={() => setActiveTab('welcome-bonus')}
          className={`px-3 md:px-4 py-2 text-sm md:text-base whitespace-nowrap ${activeTab === 'welcome-bonus' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
        >
          Welcome Bonus
        </button>
        <button
          onClick={() => setActiveTab('promotions')}
          className={`px-3 md:px-4 py-2 text-sm md:text-base whitespace-nowrap ${activeTab === 'promotions' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
        >
          Promotions
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-3 md:px-4 py-2 text-sm md:text-base whitespace-nowrap ${activeTab === 'logs' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
        >
          Logs
        </button>
        <button
          onClick={() => setActiveTab('statistics')}
          className={`px-3 md:px-4 py-2 text-sm md:text-base whitespace-nowrap ${activeTab === 'statistics' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
        >
          Statistics
        </button>
        <button
          onClick={() => setActiveTab('device-tracking')}
          className={`px-3 md:px-4 py-2 text-sm md:text-base whitespace-nowrap ${activeTab === 'device-tracking' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
        >
          Device Tracking
        </button>
      </div>

      {activeTab === 'welcome-bonus' && (
        <div className="bg-white rounded-lg shadow-sm border p-4 md:p-6">
          <h2 className="text-lg md:text-xl font-semibold mb-4">Welcome Bonus Settings</h2>
          <p className="text-sm md:text-base text-gray-600 mb-6">
            Automatically add free days when users make their first paid subscription
          </p>

          <div className="space-y-4">
            <div>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={welcomeBonus.enabled}
                  onChange={(e) => setWelcomeBonus({ ...welcomeBonus, enabled: e.target.checked })}
                  className="w-5 h-5"
                />
                <span className="text-lg font-medium">Enable Welcome Bonus</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Number of Free Days</label>
              <input
                type="number"
                value={welcomeBonus.days}
                onChange={(e) => setWelcomeBonus({ ...welcomeBonus, days: parseInt(e.target.value) })}
                className="w-full border rounded px-3 py-2"
                min="0"
                max="365"
              />
              <p className="text-xs text-gray-500 mt-1">Users will receive this many free days on their first subscription</p>
            </div>

            <button
              onClick={handleUpdateWelcomeBonus}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Save Settings
            </button>
          </div>
        </div>
      )}

      {activeTab === 'promotions' && (
        <div className="grid gap-4">
          {loading ? (
            <div className="text-center py-12">Loading promotions...</div>
          ) : promotions.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <p className="text-gray-500">No promotions found. Create your first promotion!</p>
            </div>
          ) : (
            promotions.map((promo) => (
              <div key={promo.id} className="bg-white border rounded-lg p-4 shadow-sm">
                <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                  <div className="flex-1">
                    <h3 className="text-lg md:text-xl font-semibold">{promo.promotion_name}</h3>
                    <p className="text-sm md:text-base text-gray-600 mt-1">{promo.description}</p>
                    <div className="mt-2 space-y-1">
                      <p className="text-xs md:text-sm"><strong>Free Days:</strong> {promo.free_days}</p>
                      <p className="text-xs md:text-sm"><strong>Target:</strong> {promo.recipient_filter}</p>
                      <p className="text-xs md:text-sm">
                        <strong>Period:</strong> {new Date(promo.start_date).toLocaleDateString()} - {new Date(promo.end_date).toLocaleDateString()}
                      </p>
                      <p className="text-xs md:text-sm">
                        <strong>Redemptions:</strong> {promo.current_redemptions || 0}
                        {promo.max_redemptions && ` / ${promo.max_redemptions}`}
                      </p>
                    </div>
                    <div className="mt-2">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs md:text-sm ${promo.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {promo.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                    <button
                      onClick={() => handleApplyPromotion(promo.id)}
                      disabled={!promo.is_active}
                      className="px-3 md:px-4 py-2 bg-green-100 text-green-800 rounded hover:bg-green-200 disabled:opacity-50 text-sm"
                    >
                      Apply Now
                    </button>
                    <button
                      onClick={() => {
                        setEditingPromotion(promo);
                        setPromotionForm({
                          promotion_name: promo.promotion_name,
                          description: promo.description || '',
                          free_days: promo.free_days,
                          recipient_filter: promo.recipient_filter,
                          start_date: promo.start_date?.split('T')[0] || '',
                          end_date: promo.end_date?.split('T')[0] || '',
                          is_active: promo.is_active,
                          auto_apply: promo.auto_apply || false,
                          max_redemptions: promo.max_redemptions || null
                        });
                        setShowPromotionModal(true);
                      }}
                      className="px-3 md:px-4 py-2 bg-blue-100 text-blue-800 rounded hover:bg-blue-200 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeletePromotion(promo.id)}
                      className="px-3 md:px-4 py-2 bg-red-100 text-red-800 rounded hover:bg-red-200 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">User</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Days</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Reason</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center">Loading logs...</td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500">No logs found</td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">
                        {log.user_name || 'N/A'}<br />
                        <span className="text-gray-500 text-xs">{log.user_email}</span>
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold">{log.free_days}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-block px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                          {log.grant_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">{log.reason || '-'}</td>
                      <td className="px-4 py-3 text-sm">{new Date(log.created_at).toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'statistics' && (
        <div>
          {loading ? (
            <div className="text-center py-12">Loading statistics...</div>
          ) : statistics && statistics.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {statistics.map((stat, index) => (
                <div key={index} className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 capitalize">{stat.grant_type?.replace('_', ' ')}</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Grants:</span>
                      <span className="font-bold">{stat.total_grants || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Days Given:</span>
                      <span className="font-bold">{stat.total_days_given || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Unique Users:</span>
                      <span className="font-bold">{stat.unique_users || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Emails Sent:</span>
                      <span className="font-bold">{stat.emails_sent || 0}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">No statistics available</div>
          )}
        </div>
      )}

      {activeTab === 'device-tracking' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">🔍 Search User's Devices</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="User ID (optional if email provided)"
                  value={deviceSearch.userId}
                  onChange={(e) => setDeviceSearch({ ...deviceSearch, userId: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
                <input
                  type="email"
                  placeholder="Email (optional if ID provided)"
                  value={deviceSearch.email}
                  onChange={(e) => setDeviceSearch({ ...deviceSearch, email: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
                <button
                  onClick={handleSearchUserDevices}
                  disabled={!deviceSearch.userId && !deviceSearch.email}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Search Devices
                </button>
              </div>

              {userDevices && (
                <div className="mt-4 p-4 bg-gray-50 rounded">
                  <h4 className="font-semibold mb-2">
                    {userDevices.user?.first_name} {userDevices.user?.last_name} ({userDevices.user?.email})
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">Total Devices: {userDevices.totalDevices || 0}</p>
                  {userDevices.devices?.map((device, idx) => (
                    <div key={idx} className="bg-white p-2 rounded mt-2 text-sm">
                      <p className="font-mono text-xs break-all">{device.device_id}</p>
                      <p className="text-gray-600">Last used: {new Date(device.last_used).toLocaleString()}</p>
                      <p className="text-gray-600">Login count: {device.login_count}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">📱 Search Device's Users</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Device ID"
                  value={deviceId}
                  onChange={(e) => setDeviceId(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                />
                <button
                  onClick={handleSearchDeviceUsers}
                  disabled={!deviceId}
                  className="w-full bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 disabled:opacity-50"
                >
                  Search Users
                </button>
              </div>

              {deviceUsers && (
                <div className="mt-4 p-4 bg-gray-50 rounded">
                  <h4 className="font-semibold mb-2">Accounts: {deviceUsers.totalAccounts || 0}</h4>
                  {deviceUsers.isPotentialAbuse && (
                    <div className="bg-red-100 text-red-800 px-3 py-2 rounded mb-2 text-sm">
                      ⚠️ Potential Abuse Detected
                    </div>
                  )}
                  {deviceUsers.abuseSummary && (
                    <div className="text-sm mb-2">
                      <p>Total Free Subs: {deviceUsers.abuseSummary.total_free_subscriptions}</p>
                      <p>Total Free Days: {deviceUsers.abuseSummary.total_free_days}</p>
                    </div>
                  )}
                  {deviceUsers.users?.map((user) => (
                    <div key={user.id} className="bg-white p-2 rounded mt-2 text-sm">
                      <p className="font-semibold">{user.first_name} {user.last_name}</p>
                      <p className="text-gray-600">{user.email}</p>
                      <p className="text-gray-600">Free subs received: {user.free_subs_received || 0}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4">✅ Check User Eligibility</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <input
                type="text"
                placeholder="User ID (optional)"
                value={eligibilitySearch.userId}
                onChange={(e) => setEligibilitySearch({ ...eligibilitySearch, userId: e.target.value })}
                className="border rounded px-3 py-2"
              />
              <input
                type="email"
                placeholder="Email (optional)"
                value={eligibilitySearch.email}
                onChange={(e) => setEligibilitySearch({ ...eligibilitySearch, email: e.target.value })}
                className="border rounded px-3 py-2"
              />
              <input
                type="number"
                placeholder="Promotion ID"
                value={eligibilitySearch.promotionId}
                onChange={(e) => setEligibilitySearch({ ...eligibilitySearch, promotionId: e.target.value })}
                className="border rounded px-3 py-2"
                required
              />
            </div>
            <button
              onClick={handleCheckEligibility}
              disabled={!eligibilitySearch.promotionId}
              className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 disabled:opacity-50"
            >
              Check Eligibility
            </button>

            {eligibilityResult && (
              <div className="mt-4 p-4 bg-gray-50 rounded">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${eligibilityResult.checks?.eligible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {eligibilityResult.conclusion || 'N/A'}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                  {eligibilityResult.checks && Object.entries(eligibilityResult.checks).map(([key, value]) => {
                    if (key === 'reason' || key === 'otherAccountsOnDevice' || key === 'eligible') return null;
                    return (
                      <div key={key} className="flex items-center gap-2">
                        <span className={value ? '✅' : '❌'}></span>
                        <span className="capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                      </div>
                    );
                  })}
                </div>

                {eligibilityResult.checks?.reason && eligibilityResult.checks.reason.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                    <p className="font-semibold mb-1">Reasons:</p>
                    <ul className="list-disc list-inside">
                      {eligibilityResult.checks.reason.map((r, idx) => (
                        <li key={idx}>{r}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {eligibilityResult.checks?.otherAccountsOnDevice && eligibilityResult.checks.otherAccountsOnDevice.length > 0 && (
                  <div className="mt-3 bg-orange-50 border border-orange-200 rounded p-3 text-sm">
                    <p className="font-semibold mb-1">Other accounts on this device:</p>
                    {eligibilityResult.checks.otherAccountsOnDevice.map((acc, idx) => (
                      <div key={idx} className="mt-1">
                        {acc.email} (redeemed: {new Date(acc.redeemed_at).toLocaleString()})
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-white border rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">🚨 Abuse Detection</h3>
              <button
                onClick={handleDetectAbuse}
                className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700"
              >
                Scan for Abuse
              </button>
            </div>

            {abuseDevices.length > 0 ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Found {abuseDevices.length} suspicious device(s) with multiple accounts
                </p>
                {abuseDevices.map((device, idx) => (
                  <div key={idx} className="border rounded p-4 bg-red-50">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-mono text-xs break-all text-gray-600">{device.device_id}</p>
                        <p className="text-sm mt-1">
                          <strong>{device.account_count}</strong> accounts | 
                          <strong> {device.total_free_subs}</strong> free subs | 
                          <strong> {device.total_free_days}</strong> free days
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 space-y-2">
                      {device.users?.map((user) => (
                        <div key={user.id} className="bg-white p-2 rounded text-sm">
                          <p><strong>{user.email}</strong></p>
                          <p className="text-gray-600">
                            Joined: {new Date(user.created_at).toLocaleDateString()} | 
                            Free subs: {user.free_subs_count} ({user.total_days_received} days)
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : loading ? (
              <p className="text-center py-8 text-gray-500">Scanning...</p>
            ) : (
              <p className="text-center py-8 text-gray-500">No abuse detected. Click "Scan for Abuse" to check.</p>
            )}
          </div>
        </div>
      )}

      {showPromotionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 md:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl md:text-2xl font-bold mb-4">
              {editingPromotion ? 'Edit Promotion' : 'Create Promotion'}
            </h2>
            <form onSubmit={handleCreatePromotion}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Promotion Name</label>
                  <input
                    type="text"
                    value={promotionForm.promotion_name}
                    onChange={(e) => setPromotionForm({ ...promotionForm, promotion_name: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    value={promotionForm.description}
                    onChange={(e) => setPromotionForm({ ...promotionForm, description: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    rows="2"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Free Days</label>
                    <input
                      type="number"
                      value={promotionForm.free_days}
                      onChange={(e) => setPromotionForm({ ...promotionForm, free_days: parseInt(e.target.value) })}
                      className="w-full border rounded px-3 py-2"
                      min="1"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Max Redemptions</label>
                    <input
                      type="number"
                      value={promotionForm.max_redemptions || ''}
                      onChange={(e) => setPromotionForm({ ...promotionForm, max_redemptions: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      placeholder="Unlimited"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Target Audience</label>
                  <select
                    value={promotionForm.recipient_filter}
                    onChange={(e) => setPromotionForm({ ...promotionForm, recipient_filter: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    required
                  >
                    <option value="all">All Users</option>
                    <option value="verified">Verified Users</option>
                    <option value="unverified">Unverified Users</option>
                    <option value="subscribed">Active Subscribers</option>
                    <option value="unsubscribed">Non-Subscribers</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Start Date</label>
                    <input
                      type="date"
                      value={promotionForm.start_date}
                      onChange={(e) => setPromotionForm({ ...promotionForm, start_date: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">End Date</label>
                    <input
                      type="date"
                      value={promotionForm.end_date}
                      onChange={(e) => setPromotionForm({ ...promotionForm, end_date: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={promotionForm.is_active}
                      onChange={(e) => setPromotionForm({ ...promotionForm, is_active: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm">Active</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={promotionForm.auto_apply}
                      onChange={(e) => setPromotionForm({ ...promotionForm, auto_apply: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm">Auto-apply</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowPromotionModal(false);
                    setEditingPromotion(null);
                  }}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {editingPromotion ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showGrantModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 md:p-6 w-full max-w-lg">
            <h2 className="text-xl md:text-2xl font-bold mb-4">Manual Free Subscription Grant</h2>
            <form onSubmit={handleGrantFreeSubscription}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">User IDs (comma-separated)</label>
                  <input
                    type="text"
                    value={grantForm.user_ids}
                    onChange={(e) => setGrantForm({ ...grantForm, user_ids: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="1, 2, 3, 4"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Number of Free Days</label>
                  <input
                    type="number"
                    value={grantForm.free_days}
                    onChange={(e) => setGrantForm({ ...grantForm, free_days: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    min="1"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Reason (for audit trail)</label>
                  <textarea
                    value={grantForm.reason}
                    onChange={(e) => setGrantForm({ ...grantForm, reason: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    rows="3"
                    placeholder="e.g., Compensation for service downtime"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowGrantModal(false)}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Grant Free Days
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FreeSubscriptions;
